import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { logout } from './authSlice';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3002';

const defaultMeta = {
  page: 1,
  page_size: 30,
  total_items: 0,
  total_pages: 0
};

const padNumber = (value) => String(value).padStart(2, '0');

const formatDateForInput = (date) =>
  `${date.getFullYear()}-${padNumber(date.getMonth() + 1)}-${padNumber(date.getDate())}`;

const createDefaultDateRange = () => {
  const end = new Date();
  end.setDate(end.getDate() - 1);
  const start = new Date(end);
  start.setDate(end.getDate() - 29);

  return {
    start: formatDateForInput(start),
    end: formatDateForInput(end)
  };
};

const defaultRange = createDefaultDateRange();

const defaultQuery = {
  page: 1,
  pageSize: 30,
  startDate: defaultRange.start,
  endDate: defaultRange.end,
  sortBy: 'date',
  sortDirection: 'desc'
};

const toApiDate = (value) => value || null;

const initialState = {
  items: [],
  chartItems: [],
  meta: defaultMeta,
  loading: false,
  chartLoading: false,
  error: null,
  chartError: null,
  query: defaultQuery,
  exportStatus: 'idle',
  exportError: null
};

const buildAuthHeaders = (getState, extra = {}) => {
  const { auth } = getState();
  const headers = {
    Accept: 'application/json',
    'Content-Type': 'application/json',
    ...extra
  };

  if (auth?.accessToken) {
    headers.Authorization = `Bearer ${auth.accessToken}`;
  }

  return headers;
};

const handleUnauthorized = (dispatch) => {
  dispatch(logout());
  if (typeof window !== 'undefined') {
    window.location.replace('/login');
  }
};

const handleApiResponse = async (response, dispatch) => {
  if (response.status === 401) {
    handleUnauthorized(dispatch);
    throw new Error('Unauthorized');
  }

  const contentType = response.headers.get('content-type');
  const data =
    contentType && contentType.includes('application/json')
      ? await response.json()
      : await response.text();

  if (!response.ok) {
    const message =
      (data &&
        typeof data === 'object' &&
        (data.resultDesc || data.resultMessage || data.message)) ||
      response.statusText ||
      'Request failed';
    throw new Error(message);
  }

  if (data && typeof data === 'object' && Number(data.resultCode) === 401) {
    handleUnauthorized(dispatch);
    throw new Error(data.resultDesc || data.resultMessage || data.message || 'Unauthorized');
  }

  return data;
};

const normalizeQuery = (params = {}) => ({
  page: params.page ?? defaultQuery.page,
  pageSize: params.pageSize ?? defaultQuery.pageSize,
  startDate: params.startDate ?? defaultQuery.startDate,
  endDate: params.endDate ?? defaultQuery.endDate,
  sortBy: params.sortBy ?? defaultQuery.sortBy,
  sortDirection: params.sortDirection ?? defaultQuery.sortDirection
});

const buildQueryString = (params = {}) => {
  const normalized = normalizeQuery(params);
  const search = new URLSearchParams();

  if (normalized.page != null) search.set('page', normalized.page);
  if (normalized.pageSize != null) search.set('page_size', normalized.pageSize);
  if (normalized.startDate) search.set('start_date', toApiDate(normalized.startDate));
  if (normalized.endDate) search.set('end_date', toApiDate(normalized.endDate));
  if (normalized.sortBy) search.set('sort_by', normalized.sortBy);
  if (normalized.sortDirection) search.set('sort_direction', normalized.sortDirection);

  const queryString = search.toString();
  return queryString ? `?${queryString}` : '';
};

const buildAllQueryString = (params = {}) => {
  const normalized = normalizeQuery(params);
  const search = new URLSearchParams();

  if (normalized.startDate) search.set('start_date', toApiDate(normalized.startDate));
  if (normalized.endDate) search.set('end_date', toApiDate(normalized.endDate));
  if (normalized.sortBy) search.set('sort_by', normalized.sortBy);
  if (normalized.sortDirection) search.set('sort_direction', normalized.sortDirection);
  search.set('all', 'true');

  const queryString = search.toString();
  return queryString ? `?${queryString}` : '';
};

const normalizeItem = (item) => ({
  id: item.id || item.date,
  date: item.date,
  daily: Number(item.daily ?? item.d_1 ?? 0),
  weekly: Number(item.weekly ?? item.last_7_days ?? 0),
  monthly: Number(item.monthly ?? item.last_30_days ?? 0)
});

export const fetchUniqueUsers = createAsyncThunk(
  'uniqueUsers/fetchUniqueUsers',
  async (params, { getState, dispatch, rejectWithValue }) => {
    const normalized = normalizeQuery(params);

    try {
      const queryString = buildQueryString(normalized);
      const response = await fetch(`${API_BASE_URL}/api/unique-user${queryString}`, {
        method: 'GET',
        headers: buildAuthHeaders(getState)
      });

      const data = await handleApiResponse(response, dispatch);
      const items = Array.isArray(data?.data) ? data.data.map(normalizeItem) : [];
      const rawMeta = data?.meta || defaultMeta;

      return {
        items,
        meta: {
          page: Number(rawMeta.page ?? normalized.page),
          page_size: Number(rawMeta.page_size ?? normalized.pageSize),
          total_items: Number(rawMeta.total_items ?? defaultMeta.total_items),
          total_pages: Number(rawMeta.total_pages ?? defaultMeta.total_pages)
        },
        query: normalized
      };
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to load unique user report.');
    }
  }
);

export const fetchUniqueUserChart = createAsyncThunk(
  'uniqueUsers/fetchUniqueUserChart',
  async (params, { getState, dispatch, rejectWithValue }) => {
    const normalized = normalizeQuery(params);

    try {
      const queryString = buildAllQueryString(normalized);
      const response = await fetch(`${API_BASE_URL}/api/unique-user${queryString}`, {
        method: 'GET',
        headers: buildAuthHeaders(getState)
      });

      const data = await handleApiResponse(response, dispatch);
      const items = Array.isArray(data?.data) ? data.data.map(normalizeItem) : [];

      return {
        items,
        query: normalized
      };
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to load unique user chart.');
    }
  }
);

export const exportUniqueUsers = createAsyncThunk(
  'uniqueUsers/exportUniqueUsers',
  async (params, { getState, dispatch, rejectWithValue }) => {
    const normalized = normalizeQuery(params);

    try {
      const search = new URLSearchParams();
      if (normalized.startDate) search.set('start_date', normalized.startDate);
      if (normalized.endDate) search.set('end_date', normalized.endDate);
      if (normalized.sortBy) search.set('sort_by', normalized.sortBy);
      if (normalized.sortDirection) search.set('sort_direction', normalized.sortDirection);
      search.set('all', 'true');

      const response = await fetch(`${API_BASE_URL}/api/unique-user/export?${search.toString()}`, {
        method: 'GET',
        headers: buildAuthHeaders(getState, {
          Accept:
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/json'
        })
      });

      if (!response.ok) {
        if (response.status === 401) {
          handleUnauthorized(dispatch);
        }
        const contentType = response.headers.get('content-type') || '';
        const payload = contentType.includes('application/json')
          ? await response.json()
          : await response.text();
        const message =
          (payload &&
            typeof payload === 'object' &&
            (payload.resultDesc || payload.resultMessage || payload.message)) ||
          (typeof payload === 'string' ? payload : '') ||
          response.statusText ||
          'Failed to export unique user report.';
        throw new Error(message);
      }

      const blob = await response.blob();
      if (typeof window !== 'undefined' && typeof document !== 'undefined') {
        const disposition = response.headers.get('content-disposition') || '';
        const match = disposition.match(/filename="?(.+?)"?$/i);
        const filename = match?.[1] || 'unique-user-report.xlsx';

        const url = window.URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = filename;
        document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();
        window.URL.revokeObjectURL(url);
      }

      return {};
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to export unique user report.');
    }
  }
);

const uniqueUsersSlice = createSlice({
  name: 'uniqueUsers',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchUniqueUsers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUniqueUsers.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.items = action.payload.items;
        state.meta = action.payload.meta;
        state.query = action.payload.query;
      })
      .addCase(fetchUniqueUsers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to load unique user report.';
      })
      .addCase(fetchUniqueUserChart.pending, (state) => {
        state.chartLoading = true;
        state.chartError = null;
      })
      .addCase(fetchUniqueUserChart.fulfilled, (state, action) => {
        state.chartLoading = false;
        state.chartError = null;
        state.chartItems = action.payload.items;
      })
      .addCase(fetchUniqueUserChart.rejected, (state, action) => {
        state.chartLoading = false;
        state.chartError = action.payload || 'Failed to load unique user chart.';
      })
      .addCase(exportUniqueUsers.pending, (state) => {
        state.exportStatus = 'loading';
        state.exportError = null;
      })
      .addCase(exportUniqueUsers.fulfilled, (state) => {
        state.exportStatus = 'succeeded';
        state.exportError = null;
      })
      .addCase(exportUniqueUsers.rejected, (state, action) => {
        state.exportStatus = 'failed';
        state.exportError = action.payload || 'Failed to export unique user report.';
      });
  }
});

export default uniqueUsersSlice.reducer;
